import { useId, type ReactNode } from "react";

import { Button } from "components/ui/button";

/**
 * The "Past" group of an event list: a heading, the cards the caller lays out,
 * and "Show all" when the list was cut at the newest few (event-past.ts).
 *
 * The caller owns the layout of the cards, because the lists differ (a grid of
 * cards, a stack of rows), and owns whether the list is expanded, so it can
 * hand the same flag to `splitPast`. The button is a 44px target and says
 * through `aria-expanded` which way it will go.
 */
export function PastSection({
  total,
  hasMore,
  expanded,
  onToggle,
  testId = "past-section",
  children,
}: {
  /** Every past event, shown or not. */
  total: number;
  /** More past events than are shown while collapsed. */
  hasMore: boolean;
  expanded: boolean;
  onToggle: () => void;
  testId?: string;
  children: ReactNode;
}) {
  const headingId = useId();
  const listId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="mt-8 flex flex-col gap-3"
      data-testid={testId}
    >
      <h2
        id={headingId}
        className="font-display text-xl font-normal text-foreground"
      >
        Past
      </h2>
      <div id={listId}>{children}</div>
      {hasMore && (
        <Button
          variant="outline"
          className="min-h-11 self-start"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
        >
          {expanded ? "Show fewer" : `Show all ${total}`}
        </Button>
      )}
    </section>
  );
}
